import { openDialog, pushToast } from "./dialog/DialogHost";

/**
 * Alertas de la app. Misma API que cuando esto envolvía a SweetAlert (showSuccess,
 * showWarning, showError, confirmDialog, notifyApiError…), pero encima de los diálogos
 * y toasts propios (components/dialog) que siguen el lenguaje del rediseño.
 *
 * - showSuccess → toast (arriba, se va solo).
 * - showWarning / showError → diálogo con un solo botón ("Entendido" / "Aceptar").
 * - confirmDialog → diálogo con cancelar + confirmar; `danger: true` pinta el botón de
 *   confirmar en rojo y arranca con el foco en cancelar (borrar, desconectar, cancelar cita).
 *   `details` ([[etiqueta, valor], …]) y `note` sustituyen al `html` de antes (que sigue
 *   aceptándose por compatibilidad).
 *
 * Las promesas resuelven con `{ isConfirmed, isDismissed }`; confirmDialog con un boolean.
 */

const asText = (v) => {
  if (v == null) return "";
  if (v instanceof Error) return v.message || String(v);
  if (typeof v === "object") return v.message || JSON.stringify(v);
  return String(v);
};

// Opciones al estilo SweetAlert que algún llamador pudiera seguir mandando.
const fromSwalOptions = (options = {}) => {
  const out = {};
  if (options.confirmButtonText) out.confirmText = options.confirmButtonText;
  if (options.cancelButtonText) out.cancelText = options.cancelButtonText;
  if (options.showCancelButton != null) out.showCancel = !!options.showCancelButton;
  if (options.timer) out.timer = options.timer;
  if (options.html) out.html = options.html;
  if (options.icon) out.icon = options.icon;
  if (options.danger != null) out.danger = !!options.danger;
  if (options.details) out.details = options.details;
  if (options.note) out.note = options.note;
  if (options.theme) out.theme = options.theme;
  return out;
};

export const showSuccess = (title = "Listo", text = "", options = {}) => {
  if (options.toast === false) {
    return openDialog({ icon: "success", title: asText(title), text: asText(text), confirmText: "Aceptar", ...fromSwalOptions(options) });
  }
  return pushToast({ icon: "success", title: asText(title), text: asText(text), timer: options.timer ?? 3000, ...fromSwalOptions(options) });
};

export const showWarning = (title = "Atención", text = "", options = {}) => {
  return openDialog({ icon: "warning", title: asText(title), text: asText(text), confirmText: "Entendido", ...fromSwalOptions(options) });
};

export const showError = (title = "Error", text = "", options = {}) => {
  return openDialog({ icon: "error", title: asText(title), text: asText(text), confirmText: "Aceptar", ...fromSwalOptions(options) });
};

export const showInfo = (title = "", text = "", options = {}) => {
  return openDialog({ icon: "info", title: asText(title), text: asText(text), confirmText: "Entendido", ...fromSwalOptions(options) });
};

/**
 * Alerta amigable de suscripción vencida. No es un error del sistema: el cliente
 * debe entender que solo necesita renovar su pago.
 */
export const showSubscriptionExpired = (
  text = "Tu suscripción expiró. Renueva tu pago para volver a editar.",
) => {
  return openDialog({
    icon: "warning",
    title: "Suscripción vencida",
    text: asText(text),
    note: "Tus datos, citas y configuración siguen seguros. Reactiva tu cuenta realizando tu pago y podrás volver a editar de inmediato.",
    noteTone: "accent",
    confirmText: "Entendido",
  });
};

/**
 * Construye un detalle técnico del error a partir de un error de axios.
 * Pensado para que el cliente lo capture/copie y soporte sepa qué pasó.
 */
const buildErrorDetail = (error, context = "") => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || "Error desconocido";
  const code = data?.code || (error?.response?.status ? `HTTP ${error.response.status}` : "SIN_CODIGO");
  const req = error?.config ? `${(error.config.method || "").toUpperCase()} ${error.config.url || ""}`.trim() : "";
  return [
    context && `Acción: ${context}`,
    `Código: ${code}`,
    `Detalle: ${message}`,
    req && `Petición: ${req}`,
    `Hora: ${new Date().toLocaleString("es-MX")}`,
  ].filter(Boolean).join("\n");
};

/**
 * Error con detalle visible y copiable. Reemplaza los "Por favor, reporta este
 * problema" sin contexto: el cliente ve (y puede copiar) el código y el mensaje
 * exactos para reportarlos.
 */
export const showErrorDetailed = (error, { title = "Ocurrió un error", context = "" } = {}) => {
  const detail = buildErrorDetail(error, context);
  return openDialog({
    icon: "error",
    title,
    text: "No se pudo completar la acción. Si el problema continúa, manda este detalle a soporte (o toma una captura).",
    pre: detail,
    copyText: detail,
    confirmText: "Cerrar",
  });
};

/**
 * Punto único para manejar errores de API: si la suscripción venció muestra la
 * alerta amigable; cualquier otro error muestra el detalle copiable.
 */
export const notifyApiError = (error, context = "") => {
  if (error?.response?.data?.code === "SUBSCRIPTION_INACTIVE") {
    return showSubscriptionExpired(error?.response?.data?.message);
  }
  return showErrorDetailed(error, { context });
};

/**
 * confirmDialog: conveniente para confirmaciones (eliminar, cambios irreversibles...)
 * devuelve Promise<boolean> -> true si confirmó
 */
export const confirmDialog = async ({
  title = "¿Estás seguro?",
  text = "",
  html = "",
  details,
  note,
  noteTone,
  confirmButtonText = "Sí, continuar",
  cancelButtonText = "No",
  icon = "question",
  danger = false,
  options = {},
} = {}) => {
  const res = await openDialog({
    icon,
    title: asText(title),
    text: asText(text),
    html,
    details,
    note,
    noteTone,
    confirmText: confirmButtonText,
    cancelText: cancelButtonText,
    showCancel: true,
    danger,
    ...fromSwalOptions(options),
  });
  return !!res.isConfirmed;
};

const alerts = {
  showSuccess,
  showWarning,
  showError,
  showInfo,
  showSubscriptionExpired,
  showErrorDetailed,
  notifyApiError,
  confirmDialog,
};

export default alerts;

// Solo en desarrollo: poder disparar alertas desde la consola / capturas automatizadas.
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") window.__tlAlerts = alerts;
